/**
 * PubMed `.nbib` (MEDLINE) files → reference records.
 *
 * PubMed's "Save → Citation manager" file, and its "PubMed" and "MEDLINE" display
 * formats, are tagged text: a tag padded to four characters, a hyphen, a space and a
 * value (`PMID- 25541969`, `TI  - …`, `FAU - Klein, Martin`). A value wraps onto lines
 * that start with six spaces, tags such as `FAU`, `AU`, `PT`, `MH` and `LID` repeat, and a
 * blank line ends a record. The tags are those listed in the National Library of
 * Medicine's "MEDLINE/PubMed Data Element (Field) Descriptions".
 *
 * Page ranges are abbreviated in PubMed ("1234-8" means 1234–1238); they are expanded
 * here, because RIS and BibTeX readers expect the end page in full.
 */

import {
  type ConvertResult,
  type ConvertWarning,
  composeDate,
} from "@/lib/tool-kit/research-discovery/convert";
import {
  emptyRecord,
  type RefName,
  type RefRecord,
  squash,
} from "@/lib/tool-kit/research-discovery/records";

export type NbibField = readonly [tag: string, value: string];

const RE_TAG_LINE = /^([A-Z][A-Z0-9]{1,3})\s*-\s?(.*)$/;
const RE_CONTINUATION = /^ {6}(.*)$/;
const RE_LINE_BREAK = /\r\n|\r|\n/;
const RE_ID_SUFFIX = /\s*\[(doi|pii|pmc|pubmed|mid)\]\s*$/i;
const RE_ISSN_NOTE = /\s*\((?:print|electronic|linking)\)\s*$/i;
const RE_INITIALS = /^[A-Z][A-Z-]{0,5}$/;
const RE_SUFFIX = /^(?:Jr|Sr|2nd|3rd|II|III|IV)$/i;
const RE_DIGITS = /^(\D*)(\d+)$/;
const RE_ONLY_INITIALS = /^[A-Z](?: [A-Z])*$/;
const RE_RIS_TYPE_LINE = /^TY\s+-/m;
const RE_STAR = /\*/g;
const RE_BOM = /^\uFEFF/;
const RE_RANGE_DASH = /[-\u2013]/;

/** The fields of each record, in file order. */
export function parseNbib(text: string): NbibField[][] {
  const records: NbibField[][] = [];
  let fields: [string, string][] = [];

  const flush = () => {
    if (fields.length > 0) {
      records.push(fields);
    }
    fields = [];
  };

  for (const line of text.replace(RE_BOM, "").split(RE_LINE_BREAK)) {
    if (line.trim() === "") {
      flush();
      continue;
    }
    const tag = RE_TAG_LINE.exec(line);
    if (tag) {
      if (tag[1] === "PMID" && fields.some(([name]) => name === "PMID")) {
        flush();
      }
      fields.push([tag[1], tag[2].trim()]);
      continue;
    }
    const continuation = RE_CONTINUATION.exec(line);
    const last = fields.at(-1);
    if (continuation && last) {
      last[1] = `${last[1]} ${continuation[1].trim()}`.trim();
    }
  }
  flush();
  return records;
}

function values(fields: readonly NbibField[], tag: string): string[] {
  return fields.flatMap(([name, value]) => (name === tag ? [value] : []));
}

function first(fields: readonly NbibField[], tag: string): string {
  return values(fields, tag)[0] ?? "";
}

/** "Klein, Martin" → family/given; bare initials ("J E") get their full stops. */
export function fullAuthor(raw: string): RefName {
  const comma = raw.indexOf(",");
  if (comma < 0) {
    return { family: squash(raw), given: "" };
  }
  const given = squash(raw.slice(comma + 1));
  return {
    family: squash(raw.slice(0, comma)),
    given: RE_ONLY_INITIALS.test(given)
      ? given
          .split(" ")
          .map((letter) => `${letter}.`)
          .join(" ")
      : given,
  };
}

/** "Klein M" → Klein, M.; "Smith JA Jr" → Smith, J. A.; names with no initials stay whole. */
export function abbreviatedAuthor(raw: string): RefName {
  const tokens = squash(raw).split(" ");
  const last = tokens.at(-1) ?? "";
  const tail = RE_SUFFIX.test(last) ? tokens.at(-2) : last;
  const initialsAt = RE_SUFFIX.test(last)
    ? tokens.length - 2
    : tokens.length - 1;
  if (tokens.length < 2 || !tail || !RE_INITIALS.test(tail)) {
    return { family: squash(raw), given: "" };
  }
  const given = [...tail.replaceAll("-", "")]
    .map((letter) => `${letter}.`)
    .join(" ");
  return { family: tokens.slice(0, initialsAt).join(" "), given };
}

/** PubMed ends every title with a full stop that is not part of the title. */
export function cleanTitle(title: string): string {
  return title.endsWith(".") && !title.endsWith("..")
    ? title.slice(0, -1)
    : title;
}

function authorsOf(fields: readonly NbibField[]): RefName[] {
  const full = values(fields, "FAU");
  const people =
    full.length > 0
      ? full.map(fullAuthor)
      : values(fields, "AU").map(abbreviatedAuthor);
  const groups = values(fields, "CN").map((name) => ({
    family: name,
    given: "",
    literal: name,
  }));
  return [...people, ...groups];
}

/**
 * Expand PubMed's abbreviated page range: "1234-8" → 1234, 1238; "e45-e50" and
 * "S12-S19" are returned as written.
 */
export function expandPages(pages: string): [string, string] {
  const dash = pages.search(RE_RANGE_DASH);
  if (dash < 0) {
    return [pages.trim(), ""];
  }
  const start = pages.slice(0, dash).trim();
  const end = pages.slice(dash + 1).trim();
  const from = RE_DIGITS.exec(start);
  const to = RE_DIGITS.exec(end);
  if (
    from &&
    to &&
    to[1] === "" &&
    from[1] === "" &&
    to[2].length < from[2].length
  ) {
    return [
      start,
      `${from[2].slice(0, from[2].length - to[2].length)}${to[2]}`,
    ];
  }
  return [start, end];
}

function doiOf(fields: readonly NbibField[]): string {
  for (const tag of ["AID", "LID"]) {
    for (const value of values(fields, tag)) {
      const match = RE_ID_SUFFIX.exec(value);
      if (match?.[1].toLowerCase() === "doi") {
        return value.replace(RE_ID_SUFFIX, "").trim().toLowerCase();
      }
    }
  }
  return "";
}

function publicationDate(fields: readonly NbibField[]): string {
  const published = composeDate("", first(fields, "DP"));
  if (published !== "") {
    return published;
  }
  const electronic = first(fields, "DEP");
  return electronic.length >= 6
    ? `${electronic.slice(0, 4)}/${electronic.slice(4, 6)}${electronic.length >= 8 ? `/${electronic.slice(6, 8)}` : ""}`
    : "";
}

function typeOf(fields: readonly NbibField[]): string {
  const kinds = values(fields, "PT");
  const hasBook = first(fields, "BTI") !== "";
  if (
    kinds.some((kind) => kind === "Book Chapter") ||
    (hasBook && first(fields, "TI") !== "")
  ) {
    return "CHAP";
  }
  return hasBook ? "BOOK" : "JOUR";
}

function keywordsOf(fields: readonly NbibField[]): string[] {
  const terms = [...values(fields, "OT"), ...values(fields, "MH")].map((term) =>
    squash(term.replace(RE_STAR, ""))
  );
  return [...new Set(terms.filter((term) => term !== ""))];
}

function convertRecord(
  fields: readonly NbibField[],
  index: number,
  warnings: ConvertWarning[]
): RefRecord {
  const record = emptyRecord(typeOf(fields));
  const pmid = first(fields, "PMID");
  const book = record.type === "CHAP" || record.type === "BOOK";
  record.title = cleanTitle(first(fields, "TI") || first(fields, "BTI"));
  record.authors = authorsOf(fields);
  record.editors = values(fields, "FED").map(fullAuthor);
  record.container = book
    ? first(fields, "BTI")
    : first(fields, "JT") || first(fields, "TA");
  record.containerAbbrev = book ? "" : first(fields, "TA");
  [record.startPage, record.endPage] = expandPages(first(fields, "PG"));
  record.volume = first(fields, "VI");
  record.issue = first(fields, "IP");
  record.date = publicationDate(fields);
  record.year = record.date.slice(0, 4);
  record.issn = first(fields, "IS").replace(RE_ISSN_NOTE, "");
  record.isbn = first(fields, "ISBN");
  record.publisher = first(fields, "PB");
  record.place = book ? first(fields, "PL") : "";
  record.edition = first(fields, "EN");
  record.doi = doiOf(fields);
  record.abstract = first(fields, "AB");
  record.language = first(fields, "LA");
  record.keywords = keywordsOf(fields);
  record.accession = pmid;
  record.database = pmid ? "PubMed" : "";
  record.url = pmid ? `https://pubmed.ncbi.nlm.nih.gov/${pmid}/` : "";
  const pmc = first(fields, "PMC");
  const kinds = values(fields, "PT");
  if (pmc) {
    record.notes.push(`PMCID: ${pmc}`);
  }
  if (kinds.length > 0) {
    record.notes.push(`Publication types: ${kinds.join("; ")}`);
  }
  return check(record, fields, kinds, index, warnings);
}

function check(
  record: RefRecord,
  fields: readonly NbibField[],
  kinds: readonly string[],
  index: number,
  warnings: ConvertWarning[]
): RefRecord {
  const detail = record.accession ? `PMID ${record.accession}` : undefined;
  if (kinds.includes("Retracted Publication")) {
    warnings.push({ code: "retracted", record: index, detail });
  }
  if (kinds.includes("Retraction of Publication")) {
    warnings.push({ code: "retractionNotice", record: index, detail });
  }
  if (record.title === "") {
    warnings.push({ code: "noTitle", record: index, detail });
  }
  if (record.authors.length === 0) {
    warnings.push({ code: "noAuthors", record: index, detail });
  }
  if (record.year === "" && first(fields, "DP") !== "") {
    warnings.push({
      code: "dateUnreadable",
      record: index,
      detail: first(fields, "DP"),
    });
  }
  return record;
}

/** Convert a PubMed .nbib / MEDLINE-format export. */
export function convertNbib(source: string): ConvertResult {
  if (source.trim() === "") {
    return { records: [], warnings: [], sourceCount: 0, problem: "empty" };
  }
  const parsed = parseNbib(source).filter((fields) =>
    fields.some(([tag]) => tag === "PMID")
  );
  if (parsed.length === 0) {
    const problem = RE_RIS_TYPE_LINE.test(source)
      ? "looksLikeRis"
      : "noRecords";
    return { records: [], warnings: [], sourceCount: 0, problem };
  }
  const warnings: ConvertWarning[] = [];
  const records = parsed.map((fields, index) =>
    convertRecord(fields, index + 1, warnings)
  );
  return { records, warnings, sourceCount: parsed.length };
}
